/** Consent detection (CMP) and state resolution for the tracking script. */

import { state, debugLog } from "./state.js";
import { cookieDomain } from "./cookies.js";
import { CONSENT_COOKIE_NAME, CONSENT_COOKIE_MAX_AGE_DAYS, CMP_CONSENT_COOKIE_NAME,
  cmpConsentCookieValue, encodeConsentCookie, consentFromCookieString } from "./consent-cookie.js";
import { loadShopifyPrivacy } from "./shopify-privacy.js";
import { clearGoogleSession } from "./google-session-browser.js";
import { consentFromDataLayer } from "./consent-data-layer.js";

/** Resolved consent state for the current visitor. */
export interface ConsentState {
  analytics: "granted" | "denied" | "not_set";
  marketing: "granted" | "denied" | "not_set";
}

/**
 * Waar de eigen keuze van de bezoeker blijft staan.
 *
 * Nodig omdat de meeste kleine sites een zelfgebouwde banner hebben. Die praat
 * met niemand: geen Cookiebot, geen OneTrust, geen Consent Mode. Zo'n site kon
 * bij ons alleen kiezen tussen altijd meten en nooit meten, en allebei is fout.
 * Met `TheSocialTap.consent(...)` kan de banner het gewoon zeggen.
 */
const CONSENT_STORAGE_KEY = "thesocialtap-consent";

/** Wat de site zelf heeft doorgegeven, voor de rest van deze paginaweergave. */
let declaredConsent: ConsentState | null = null;
let lastCmpCookieValue: string | null = null;
let lastCmpCookieAt = 0;

/** Keep only a recent, explicitly observed CMP state for the Woo order hook. */
function syncCmpConsentCookie(detected: ConsentState | null): void {
  if (!state.wooCommercePlugin) return;
  try {
    if (!detected) {
      if (lastCmpCookieValue !== null || document.cookie.includes(`${CMP_CONSENT_COOKIE_NAME}=`)) {
        const domain = cookieDomain();
        document.cookie = `${CMP_CONSENT_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`
          + (window.location.protocol === "https:" ? "; Secure" : "")
          + (domain ? `; domain=${domain}` : "");
      }
      lastCmpCookieValue = null;
      lastCmpCookieAt = 0;
      return;
    }
    const domain = cookieDomain();
    const value = cmpConsentCookieValue(detected, domain, window.location.protocol === "https:");
    if (value === lastCmpCookieValue && Date.now() - lastCmpCookieAt < 5 * 60 * 1000) return;
    document.cookie = value;
    lastCmpCookieValue = value;
    lastCmpCookieAt = Date.now();
  } catch { /* Cookie storage unavailable; unknown remains unknown to WooCommerce. */ }
}

/** De eerder bewaarde keuze, één keer per paginaweergave gelezen. */
function storedConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ConsentState>;
      if (parsed.analytics && parsed.marketing) {
        return { analytics: parsed.analytics, marketing: parsed.marketing };
      }
    }
  } catch { /* geen localStorage; de cookie hieronder kan er alsnog zijn */ }
  // localStorage is per-origin: een keuze op de marketingsite bestond op
  // app.klant.nl simpelweg niet, en dat subdomein bleef eeuwig op de
  // standaardstand (meestal denied) hangen. De cookie op het hoofddomein
  // draagt dezelfde keuze naar elk subdomein.
  try {
    const fromCookie = consentFromCookieString(document.cookie);
    if (fromCookie) {
      debugLog("consent", "restored from domain cookie", fromCookie);
      return fromCookie;
    }
  } catch { /* geen document.cookie-toegang */ }
  return null;
}

/**
 * De keuze van de bezoeker vastleggen.
 *
 * Wordt aangeroepen vanuit `window.TheSocialTap.consent(...)`. De waarde gaat
 * ook naar localStorage, want anders begint elke volgende pagina weer bij de
 * standaardwaarde en moet de banner op elke pagina opnieuw praten.
 */
export function declareConsent(next: ConsentState): void {
  declaredConsent = next;
  if (next.marketing !== "granted") clearGoogleSession();
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(next));
  } catch { /* geen opslag beschikbaar, dan geldt het alleen deze pagina */ }
  // Ook als domein-cookie, zodat de keuze op elk subdomein geldt. Bewust
  // rechtstreeks geschreven en niet via de consent-gegate cookie-queue: dit
  // IS de toestemmingskeuze, hij bevat geen identifier, en ook een weigering
  // hoort onthouden te worden (anders vraagt de banner opnieuw).
  try {
    const domain = cookieDomain();
    document.cookie =
      CONSENT_COOKIE_NAME +
      "=" +
      encodeConsentCookie(next) +
      "; path=/; max-age=" +
      CONSENT_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60 +
      "; SameSite=Lax" +
      (window.location.protocol === "https:" ? "; Secure" : "") +
      (domain ? "; domain=" + domain : "");
  } catch { /* geen cookies; localStorage draagt de keuze dan alleen op deze origin */ }
  debugLog("consent", "declared by site", next);
}

/**
 * Wat de bezoeker eerder over marketing heeft gezegd, of niets.
 *
 * Bruikbaar vóór de configuratie binnen is, want dit leest alleen wat de site
 * ons zelf heeft doorgegeven en wat daarvan bewaard is. De server heeft het op
 * dat moment nodig om te weten of hij een cookie mag zetten.
 */
export function declaredMarketingConsent(): "granted" | "denied" | null {
  const known = declaredConsent || storedConsent();
  if (!known) return null;
  if (known.marketing === "granted") return "granted";
  if (known.marketing === "denied") return "denied";
  return null;
}

/** Detect consent from common CMP banners (Google Consent Mode, Cookiebot, OneTrust). */
function detectConsentFromCMP(): ConsentState | null {
  // Shopify is authoritative for its native banner and integrated CMPs.
  try {
    const privacy = window.Shopify?.customerPrivacy;
    if (typeof privacy?.analyticsProcessingAllowed === "function" &&
        typeof privacy.marketingAllowed === "function" &&
        typeof privacy.saleOfDataAllowed === "function") {
      const analytics = privacy.analyticsProcessingAllowed();
      const marketing = privacy.marketingAllowed();
      const sale = privacy.saleOfDataAllowed();
      if (typeof analytics === "boolean" && typeof marketing === "boolean" && typeof sale === "boolean") {
        const permission: ConsentState = { analytics: "denied", marketing: "denied" };
        if (analytics) permission.analytics = "granted";
        if (marketing && sale) permission.marketing = "granted";
        return permission;
      }
    }
  } catch { /* API unavailable: keep looking for an explicit supported signal. */ }

  // Cookiebot
  try {
    if (window.Cookiebot && window.Cookiebot.consent) {
      const cb = window.Cookiebot.consent;
      return {
        analytics: cb.statistics ? "granted" : "denied",
        marketing: cb.marketing ? "granted" : "denied",
      };
    }
  } catch { /* ignore */ }

  // OneTrust
  try {
    if (window.OnetrustActiveGroups) {
      const groups = window.OnetrustActiveGroups;
      return {
        analytics: groups.includes(",C0002,") ? "granted" : "denied",
        marketing: groups.includes(",C0004,") ? "granted" : "denied",
      };
    }
  } catch { /* ignore */ }

  // Read a CMP's direct API before the shared dataLayer: another plugin can
  // append its own Consent Mode defaults and updates after the visitor chose.
  try {
    if (window.dataLayer && !state.pixelManagerPresent) return consentFromDataLayer(window.dataLayer);
  } catch { /* ignore */ }

  return null;
}

/** Resolve current consent state based on CMP detection and defaults. */
export function resolveConsent(): ConsentState {
  const config = state.config?.consent;
  if (!config) {
    syncCmpConsentCookie(null);
    return { analytics: "not_set", marketing: "not_set" };
  }

  // De site die het zelf zegt gaat voor alles. Dat is de enige bron die per
  // definitie klopt: hij komt van de banner die de bezoeker echt gezien heeft.
  const declared = declaredConsent || storedConsent();
  if (declared) return declared;

  // Try CMP detection first
  if (config.autoConsentReading) {
    const detected = detectConsentFromCMP();
    syncCmpConsentCookie(detected);
    if (detected) {
      debugLog("consent", "detected from CMP", detected);
      return detected;
    }
  } else {
    syncCmpConsentCookie(null);
  }

  // Fall back to configured defaults
  return {
    analytics: config.defaultAnalyticsConsent as ConsentState["analytics"],
    marketing: config.defaultMarketingConsent as ConsentState["marketing"],
  };
}

/**
 * Heeft de bezoeker zelf iets gekozen?
 *
 * Onwaar betekent: wat `resolveConsent` teruggeeft komt uit de standaardstand
 * in de instellingen, niet uit een klik op een banner. Dat verschil is het
 * verschil tussen "nee" en "nog niets gezegd", en events uit die tweede
 * toestand horen bewaard te worden in plaats van weggegooid.
 */
export function consentIsDecided(): boolean {
  return consentSource() !== "default";
}

/**
 * Waar de toestemming vandaan kwam die we zojuist gebruikten.
 *
 * Gaat mee met elk event, zodat het dashboard kan laten zien wat er echt
 * gebeurt in plaats van wat er ingesteld staat. `default` betekent: we hebben
 * niemand gehoord en vallen terug op de standaardstand. Dat is precies de
 * toestand waarin een klant denkt dat zijn banner werkt terwijl wij hem niet
 * kunnen zien.
 */
export function consentSource(): "cmp" | "declared" | "default" {
  if (declaredConsent || storedConsent()) return "declared";
  if (state.config?.consent?.autoConsentReading && detectConsentFromCMP()) return "cmp";
  return "default";
}

/** Check if marketing activities (browser pixels) are allowed. */
export function isMarketingAllowed(): boolean {
  const consent = resolveConsent();
  return consent.marketing !== "denied";
}

/** Check if analytics tracking is allowed at all. */
export function isAnalyticsAllowed(): boolean {
  const consent = resolveConsent();
  return consent.analytics !== "denied";
}

/** Get URL params that should be stripped before tracking. */
export function getParamsToRemove(): string[] {
  const config = state.config?.consent;
  if (!config?.removeUrlParams) return [];
  return config.removeUrlParams.split(",").map(function (p) { return p.trim(); }).filter(Boolean);
}

// How long and how often the fallback watcher re-checks consent for CMPs
// without a usable event API (visitor decides on the banner mid-session)
const CONSENT_WATCH_INTERVAL_MS = 2000;
const CONSENT_WATCH_MAX_MS = 10 * 60 * 1000;

/** De hercontroles die op een wijziging wachten. */
const watchers: Array<() => void> = [];

/**
 * Nu meteen kijken of de toestemming veranderd is.
 *
 * Nodig wanneer de site het zelf doorgeeft via `TheSocialTap.consent(...)`.
 * De bewaakte hercontrole draait op een interval van twee seconden, en dat is
 * ruim genoeg om de klik te missen die direct op het accepteren volgt.
 */
export function runConsentChecks(): void {
  for (const check of watchers) {
    check();
  }
}

/**
 * Invoke the callback when the visitor's consent state changes after page
 * load (e.g. accepting the cookie banner). Pixel SDK loads are gated on
 * consent, so a late grant must trigger a (re-)load.
 *
 * Event hooks cover Cookiebot and OneTrust; a bounded interval re-check
 * covers Consent Mode pushes and any CMP without events. All paths are
 * guarded by a state comparison, so duplicate signals are harmless.
 */
export function onConsentChange(callback: () => void): void {
  let last = resolveConsent();

  const check = (): void => {
    if (state.config?.consent?.autoConsentReading) loadShopifyPrivacy(check);
    const current = resolveConsent();
    if (current.analytics !== last.analytics || current.marketing !== last.marketing) {
      if (current.marketing !== "granted") clearGoogleSession();
      debugLog("consent", "changed", last, "->", current);
      last = current;
      callback();
    }
  };

  watchers.push(check);

  try {
    window.addEventListener("CookiebotOnAccept", check);
    window.addEventListener("CookiebotOnDecline", check);
    window.addEventListener("OneTrustGroupsUpdated", check);
    document.addEventListener("visitorConsentCollected", check);
  } catch { /* no window events available */ }

  // A theme without Shopify's native banner may not preload this API.
  // Without it, a correctly enabled embed waits on defaults forever.
  if (state.config?.consent?.autoConsentReading) loadShopifyPrivacy(check);

  const interval = setInterval(check, CONSENT_WATCH_INTERVAL_MS);
  setTimeout(() => clearInterval(interval), CONSENT_WATCH_MAX_MS);
}
